import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme types
const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Initial state
const initialState = {
  theme: localStorage.getItem('theme') || THEMES.LIGHT,
  systemTheme: 'light',
};

// Create context
const ThemeContext = createContext();

// Provider component
export const ThemeProvider = ({ children }) => {
  const [state, setState] = useState(initialState);

  // Get system theme
  const getSystemTheme = () => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  };

  // Apply theme to document
  const applyTheme = (theme) => {
    const root = document.documentElement;
    const isDark = theme === 'dark';
    
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    
    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDark ? '#0f172a' : '#ffffff');
    }
  };

  // Set theme
  const setTheme = (theme) => {
    const effectiveTheme = theme === THEMES.SYSTEM ? state.systemTheme : theme;
    
    setState(prev => ({ ...prev, theme }));
    localStorage.setItem('theme', theme);
    applyTheme(effectiveTheme);
  };

  // Toggle theme
  const toggleTheme = () => {
    const currentTheme = state.theme;
    const newTheme = currentTheme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT;
    setTheme(newTheme);
  };

  // Initialize theme
  useEffect(() => {
    // Get system theme
    const systemTheme = getSystemTheme();
    setState(prev => ({ ...prev, systemTheme }));

    // Apply initial theme
    const savedTheme = localStorage.getItem('theme') || THEMES.LIGHT;
    const effectiveTheme = savedTheme === THEMES.SYSTEM ? systemTheme : savedTheme;
    applyTheme(effectiveTheme);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const newSystemTheme = e.matches ? 'dark' : 'light';
      setState(prev => ({ ...prev, systemTheme: newSystemTheme }));
      
      if (state.theme === THEMES.SYSTEM) {
        applyTheme(newSystemTheme);
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  // Context value
  const value = {
    theme: state.theme,
    systemTheme: state.systemTheme,
    isDark: (state.theme === THEMES.SYSTEM ? state.systemTheme : state.theme) === 'dark',
    setTheme,
    toggleTheme,
    THEMES,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 